import * as React from 'react';

import { isLeft, isRight } from '@shared-commons/utils';
import { useAuthStore } from '@/auth';
import { Dashboard } from '@/shared/i18n';

import { useDashboardStore } from '../store/dashboard-store';
import { getCustomerSubscription } from '../services/customer/get-customer-subscription';
import { PaginationQueryParams } from '../types/pagination';
import { getFavouriteLearningPath } from '../services/learning-path/get-favourite-learning-paths';
import { getFavouriteLessons } from '../services/lessons/get-favourite-lessons';
import { getSeenContents } from '../services/seen-contents/get-seen-contents';
import { getTrendingSeries } from '../services/trending-series/get-trending-series';
import { getUpComingLives } from '../services/up-coming-lives/get-up-coming-lives';
import { getContentsInProgress } from '../services/contents-in-progress/get-contents-in-progress';
import { createDashboardFavourite } from '../services/favourite/create-favourite';
import { deleteDashboardFavourite } from '../services/favourite/delete-favourite';
import { Auth } from 'firebase/auth';

export const useDashboard = (dict: Dashboard, auth: Auth) => {
  const {
    customerSubscription,
    setCustomerSubscription,
    setFavouriteLessons,
    setFavouriteLearningPath,
    setSeenContents,
    setContentsInProgress,
    setTrendingSeries,
    setFavouriteDashboard,
    setUpComingLives,
    resetStore,
  } = useDashboardStore();
  const { user } = useAuthStore();

  const fetchCustomerSubscription = React.useCallback(
    //chiama questa
    async ({ email }: { email: string }) => {
      setCustomerSubscription({ status: { isError: false, isLoading: true } });
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await getCustomerSubscription(email, accessToken)();
      if (isRight(result)) {
        setCustomerSubscription({
          data: result?.value || null,
          error: null,
          status: { isError: false, isLoading: false },
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        setCustomerSubscription({ error: error, status: { isError: true, isLoading: false } });
      }
    },
    [auth, user?.email, setCustomerSubscription],
  );

  const fetchFavouriteLearningPath = React.useCallback(
    async (pagination: PaginationQueryParams) => {
      setFavouriteLearningPath({ status: { isError: false, isLoading: true } });
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await getFavouriteLearningPath(user?.email, pagination, accessToken)();
      if (isRight(result)) {
        setFavouriteLearningPath({
          data: result?.value || null,
          error: null,
          status: { isError: false, isLoading: false },
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        setFavouriteLearningPath({ error: error, status: { isError: true, isLoading: false } });
      }
    },
    [auth, user?.email, setFavouriteLearningPath],
  );

  const fetchFavouriteLessons = React.useCallback(
    async (pagination: PaginationQueryParams) => {
      setFavouriteLessons({ status: { isError: false, isLoading: true } });
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await getFavouriteLessons(user?.email, pagination, accessToken)();
      if (isRight(result)) {
        setFavouriteLessons({
          data: result?.value || null,
          error: null,
          status: { isError: false, isLoading: false },
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        setFavouriteLessons({ error: error, status: { isError: true, isLoading: false } });
      }
    },
    [auth, user?.email, setFavouriteLessons],
  );

  const fetchSeenContents = React.useCallback(
    async (pagination: PaginationQueryParams) => {
      setSeenContents({ status: { isError: false, isLoading: true } });
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await getSeenContents(user?.email, pagination, accessToken)();
      if (isRight(result)) {
        setSeenContents({
          data: result?.value || null,
          error: null,
          status: { isError: false, isLoading: false },
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        setSeenContents({ error: error, status: { isError: true, isLoading: false } });
      }
    },
    [auth, user?.email, setSeenContents],
  );

  const fetchTrendingSeries = React.useCallback(
    async (pagination: PaginationQueryParams) => {
      setTrendingSeries({ status: { isError: false, isLoading: true } });
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await getTrendingSeries(user?.email, pagination, accessToken)();
      if (isRight(result)) {
        setTrendingSeries({
          data: result?.value || null,
          error: null,
          status: { isError: false, isLoading: false },
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        setTrendingSeries({ error: error, status: { isError: true, isLoading: false } });
      }
    },
    [auth, setTrendingSeries],
  );

  const fetchUpComingLives = React.useCallback(
    async (pagination: PaginationQueryParams) => {
      setUpComingLives({ status: { isError: false, isLoading: true } });
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await getUpComingLives(pagination, accessToken)();
      if (isRight(result)) {
        setUpComingLives({
          data: result?.value || null,
          error: null,
          status: { isError: false, isLoading: false },
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        setUpComingLives({ error: error, status: { isError: true, isLoading: false } });
      }
    },
    [auth, setUpComingLives],
  );
  const fetchContentsInProgress = React.useCallback(
    async (pagination: PaginationQueryParams) => {
      setContentsInProgress({ status: { isError: false, isLoading: true } });
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await getContentsInProgress(user?.email, pagination, accessToken)();
      if (isRight(result)) {
        setContentsInProgress({
          data: result?.value || null,
          error: null,
          status: { isError: false, isLoading: false },
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        setContentsInProgress({ error: error, status: { isError: true, isLoading: false } });
      }
    },
    [auth, user?.email, setContentsInProgress],
  );

  // CREATE FAVOURITE
  const createFavourite = React.useCallback(
    async ({ documentId }: { documentId: string }) => {
      setFavouriteDashboard({ isLoading: true });
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await createDashboardFavourite(user?.email, documentId, accessToken)();
      if (isRight(result)) {
        setFavouriteDashboard({
          isLoading: false,
          isFavourite: result.value.isFavourite,
          documentId: result.value.documentId,
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        console.log('error occurred', error);
        setFavouriteDashboard({ isLoading: false });
      }
    },
    [auth, user?.email, setFavouriteDashboard],
  );

  // DELETE FAVOURITE
  const deleteFavourite = React.useCallback(
    async ({ documentId }: { documentId: string }) => {
      setFavouriteDashboard({ isLoading: true });
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await deleteDashboardFavourite(documentId, accessToken)();
      if (isRight(result)) {
        setFavouriteDashboard({ isLoading: false, isFavourite: false, documentId: null });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'An error has occurred');
        console.log('error occurred', error);
        setFavouriteDashboard({ isLoading: false });
      }
    },
    [auth, setFavouriteDashboard],
  );

  return {
    customerSubscription,
    fetchCustomerSubscription,
    fetchFavouriteLearningPath,
    fetchFavouriteLessons,
    fetchSeenContents,
    fetchTrendingSeries,
    fetchUpComingLives,
    fetchContentsInProgress,
    createFavourite,
    deleteFavourite,
    resetStore,
  };
};
